/**
 * Generic delegated-click handler factory for the Native Interactive Site Map listing panel.
 *
 * Event delegation over a stable root element (the panel content is re-rendered via innerHTML
 * swaps on every chip/tab/lot change, so listeners bind once on the panel container instead of
 * being re-attached per render). Pure DOM — no Redux, no CSS, no business logic; the matched
 * element and native event are handed to the caller's callback, which decides what to read off
 * them. Generalizes handlers/getNotifiedInteractionHandlers.js's bind/unbind shape so it can be
 * reused for multiple different selectors (the same move already made for
 * handlers/scrollHandlers.js, reused as chipScrollHandlers/cardsScrollHandlers).
 *
 * @module nativeInteractiveSiteMap/handlers/delegatedClickHandlers
 */

/**
 * @param {string} selector - CSS selector to match via Element.closest() on the click target.
 * @returns {{ bind: Function, unbind: Function }}
 */
function createDelegatedClickHandlers(selector) {
  const state = {
    rootElement: null,
    clickHandler: null,
  };

  function unbind() {
    if (state.rootElement && state.clickHandler) {
      state.rootElement.removeEventListener('click', state.clickHandler);
    }
    state.rootElement = null;
    state.clickHandler = null;
  }

  /**
   * @param {HTMLElement} rootElement - the stable panel container to delegate from
   * @param {(matchedElement: HTMLElement, event: MouseEvent) => void} onMatch
   */
  function bind(rootElement, onMatch) {
    unbind();
    state.rootElement = rootElement;
    state.clickHandler = (event) => {
      const matchedElement = event.target instanceof Element ? event.target.closest(selector) : null;
      if (matchedElement) {
        onMatch(matchedElement, event);
      }
    };
    rootElement.addEventListener('click', state.clickHandler);
  }

  return { bind, unbind };
}

export default createDelegatedClickHandlers;
